import * as React from 'react';
import { useEffect, useState, lazy, Suspense } from 'react';
import { DeviceType, Vertical, Subvertical } from '../../shared/types/ast';
import { SearchSummary } from '../../shared/types/adsense';
import { ErrorBoundary } from './error-boundary';
import { Placeholder } from './placeholder';
import { Fallback } from './fallback';
import { loadCompanionComponent } from '../utils/component/load';

import {
    RESULT_LIST_SPACE_1,
    RESULT_LIST_SPACE_2,
    RESULT_LIST_SPACE_3,
    RESULT_LIST_ALT_SPACE_3,
    RESULT_LIST_SPACE_4,
    RESULT_LIST_ALT_SPACE_4,
    RESULT_LIST_SPACE_5,
    bannerName,
} from '../utils/result';
import { getFeatureStatus } from '../config';

const LazySearchResultBanners = lazy(() =>
    loadCompanionComponent('SearchResultBanners'),
);

export type SearchResultBannersComponent = React.FunctionComponent<Props>;

export type Props = {
    cols?: 1 | 2 | 3;
    vertical?: Vertical;
    subvertical?: Subvertical;
    deviceType?: DeviceType;
    searchSummary?: SearchSummary;
    children: React.ReactNode[];
};

const SearchResultBannersLoader: React.FC<Props> = (props) => {
    const { children } = props;

    return (
        <Suspense
            fallback={
                <SearchResultBannersServer {...props}>
                    {children}
                </SearchResultBannersServer>
            }
        >
            <LazySearchResultBanners {...props}>
                {children}
            </LazySearchResultBanners>
        </Suspense>
    );
};

const SearchResultBanners: React.FC<Props> = (props) => {
    const { children } = props;
    const [showBanners, setShowBanners] = useState(false);

    // Wait until after client-side hydration to show banners
    useEffect(() => {
        setShowBanners(true);
    }, []);

    return showBanners ? (
        <SearchResultBannersLoader {...props}>
            {children}
        </SearchResultBannersLoader>
    ) : (
        <SearchResultBannersServer {...props}>
            {children}
        </SearchResultBannersServer>
    );
};

type SpacingProps = {
    spacer: (id: string, index: number) => JSX.Element;
    cols?: 1 | 2 | 3;
    vertical?: Vertical;
    subvertical?: Subvertical;
    deviceType: DeviceType;
};

export const SearchResultBannersSpacing: React.FC<SpacingProps> = (props) => {
    const { spacer, deviceType, subvertical } = props;

    const altSpacingToggleEnabled = getFeatureStatus('enableAdIntervalVariant');
    const enableAdIntervalVariant =
        altSpacingToggleEnabled && deviceType === 'mobile';

    let adjustedSpace1 = RESULT_LIST_SPACE_1;
    let adjustedSpace2 = RESULT_LIST_SPACE_2;
    let adjustedSpace3 = enableAdIntervalVariant
        ? RESULT_LIST_ALT_SPACE_3
        : RESULT_LIST_SPACE_3;
    let adjustedSpace4 = enableAdIntervalVariant
        ? RESULT_LIST_ALT_SPACE_4
        : RESULT_LIST_SPACE_4;
    let adjustedSpace5 = RESULT_LIST_SPACE_5;

    // adjust for car rental widget
    if (subvertical === 'holiday_rental') {
        adjustedSpace2 = adjustedSpace2 + 1;
        adjustedSpace3 = adjustedSpace3 + 1;
        adjustedSpace4 = adjustedSpace4 + 1;
        adjustedSpace5 = adjustedSpace5 + 1;
    }

    const children = React.Children.toArray(props.children);

    let firstBatch: React.ReactNode[] = [];
    let secondBatch: React.ReactNode[] = [];
    let thirdBatch: React.ReactNode[] = [];
    let fourthBatch: React.ReactNode[] = [];
    let fifthBatch: React.ReactNode[] = [];
    let lastBatch: React.ReactNode[] = [];
    let firstBanner;
    let secondBanner;
    let thirdBanner;
    let fourthBanner;
    let fifthBanner;

    if (children.length > adjustedSpace1) {
        firstBatch = children.slice(0, adjustedSpace1);
        firstBanner = spacer(bannerName(deviceType, 1), 1);
    } else {
        firstBatch = children.slice(0);
    }

    if (children.length > adjustedSpace2) {
        secondBatch = children.slice(adjustedSpace1, adjustedSpace2);
        secondBanner = spacer(bannerName(deviceType, 2), 2);
    } else if (children.length > adjustedSpace1) {
        secondBatch = children.slice(adjustedSpace1);
    }

    if (children.length > adjustedSpace3) {
        thirdBatch = children.slice(adjustedSpace2, adjustedSpace3);
        thirdBanner = spacer(bannerName(deviceType, 3), 3);
    } else if (children.length > adjustedSpace2) {
        thirdBatch = children.slice(adjustedSpace2);
    }

    if (children.length > adjustedSpace4) {
        fourthBatch = children.slice(adjustedSpace3, adjustedSpace4);
        fourthBanner = spacer(bannerName(deviceType, 4), 4);
    } else if (children.length > adjustedSpace3) {
        fourthBatch = children.slice(adjustedSpace3);
    }

    if (children.length > adjustedSpace5) {
        fifthBatch = children.slice(adjustedSpace4, adjustedSpace5);
        fifthBanner = spacer(bannerName(deviceType, 5), 5);
        lastBatch = children.slice(adjustedSpace5);
    } else if (children.length > adjustedSpace4) {
        fifthBatch = children.slice(adjustedSpace4);
    }

    return (
        <>
            {firstBatch}
            {firstBanner}
            {secondBatch}
            {secondBanner}
            {thirdBatch}
            {thirdBanner}
            {fourthBatch}
            {fourthBanner}
            {fifthBatch}
            {fifthBanner}
            {lastBatch}
        </>
    );
};

const SearchResultBannersServer: React.FC<Props> = (props) => {
    const { deviceType = 'desktop', cols, vertical, subvertical } = props;
    const children = React.Children.toArray(props.children);

    return (
        <SearchResultBannersSpacing
            deviceType={deviceType}
            cols={cols}
            vertical={vertical}
            subvertical={subvertical}
            spacer={(id) => (
                <Placeholder id={id}>
                    <Fallback id={id} deviceType={deviceType} />
                </Placeholder>
            )}
        >
            {children}
        </SearchResultBannersSpacing>
    );
};

const SearchResultBannersWithErrorBoundary: React.FC<Props> = (props) => {
    return (
        <ErrorBoundary fallback={props.children}>
            <SearchResultBanners {...props} />
        </ErrorBoundary>
    );
};

export default SearchResultBannersWithErrorBoundary;
